// Sample Security Audit job for the task-type gallery (/agents/task-types/security_audit). Not a real job: it is the
// AAML sweep of 2026-10-23 (AAML-backend-app #55–#58, AAML-colorado-chapter #46/#48) told as one audit of the AAML
// system, so the report, the decisions and the verification have real findings to show. No database, no writes.

import type { AiJob } from '../../../../lib/supabase.types';
import type { Decision, OperatorSummary, SecurityFinding } from '../../../../lib/aiJobSummary';

export const SECURITY_AUDIT_CLIENT_NAME = 'AAML';

const FINDINGS: SecurityFinding[] = [
  {
    id: 'F1',
    section: 'security',
    bucket: 'database',
    title: 'Anyone can sign up and make themselves admin',
    severity: 'public',
    exposed: 'A stranger signs up on the public endpoint with roles in the metadata and gets the whole admin: users, posts, uploads.',
    proof: {
      request: 'POST https://echldzywqxjgpgwcrdqc.supabase.co/auth/v1/signup  (anon key)\n{"email":"probe+f1@bullfinch.io","password":"…","data":{"roles":["admin"]}}\nthen GET /rest/v1/users?id=eq.<new id>&select=roles  (its token)',
      result: '200 {"id":"3f1c…"}\n200 [{"roles":["admin"]}]',
    },
    fix: 'Public signup off; the signup trigger ignores client roles; a guard trigger refuses any role change not made by an admin.',
    could_break: 'Add User in the admin (it used signup): moves to a server-side create-user function in the same PR.',
    decision_id: 'D1',
  },
  {
    id: 'F2',
    section: 'security',
    bucket: 'database',
    title: 'Every user’s email, phone and roles are readable with the public key',
    severity: 'public',
    exposed: 'Anyone with the site’s anon key (it is in every page) downloads the full member list with emails and phone numbers.',
    proof: {
      request: 'GET /rest/v1/users?select=email,phone,roles  (anon key only)',
      result: '200, 214 rows: [{"email":"c…@cofamilylaw.org","phone":"303…","roles":["admin"]}, …]',
    },
    fix: 'Anon reads only id, first_name, last_name, lawyer_profile_id and avatar_url (a column grant).',
    could_break: 'The chapter event page selects the organizer’s email (deploy chapter #46 first); any anon policy that reads users.roles.',
    decision_id: 'D2',
  },
  {
    id: 'F3',
    section: 'security',
    bucket: 'database',
    title: 'Anyone can add, change or delete the admin notification list',
    severity: 'public',
    exposed: 'A stranger adds their own address to every admin notification, or deletes the list so nobody hears about pending posts.',
    proof: {
      request: 'BEGIN; SET ROLE anon; INSERT INTO notification_recipients (email) VALUES (\'probe@example.com\') RETURNING id; ROLLBACK;',
      result: 'INSERT 0 1 (id 9). Rolled back; the live REST equivalent returns 201.',
    },
    fix: 'notification_recipients becomes admin-only for every operation.',
    could_break: 'Nothing found reading it outside the admin Settings page, which runs as an admin.',
    decision_id: 'D3',
  },
  {
    id: 'F4',
    section: 'security',
    bucket: 'live_config',
    title: 'The Netlify build hook is readable with the public key',
    severity: 'public',
    exposed: 'A stranger can trigger unlimited rebuilds of the public site, burning build minutes and deploying whatever is on main.',
    proof: {
      request: 'GET /rest/v1/form_settings?select=build_hook_url  (anon key only)',
      result: '200 [{"build_hook_url":"https://api.netlify.com/build_hooks/65f…"}]',
    },
    fix: 'Hide the column from anon and non-staff, and rotate the hook (the old one is deleted).',
    could_break: 'The admin’s Publish button reads the hook; it runs as staff, so it keeps working.',
    decision_id: 'D4',
  },
  {
    id: 'F5',
    section: 'security',
    bucket: 'codebase',
    title: 'notify-admin-pending emails every admin whatever HTML it is sent',
    severity: 'public',
    exposed: 'Anyone can send a phishing email to every AAML admin from the AAML address, with any links they like.',
    proof: {
      request: 'GET /v1/projects/echldzywqxjgpgwcrdqc/functions/notify-admin-pending  (management API)\nthen read the deployed v4 source',
      result: 'verify_jwt: false; the handler passes payload.html straight to Resend for every admin. Not called: it sends email.',
    },
    fix: 'Re-read the row, send only for a fresh pending row, escape everything, mail the notification_recipients list. PR, then deploy.',
    could_break: 'The review email admins get for pending posts: same email, built from the row instead of the payload.',
    decision_id: 'D5',
  },
  {
    id: 'F6',
    section: 'security',
    bucket: 'codebase',
    title: 'The login page redirects to any site after sign-in',
    severity: 'public',
    exposed: 'A phishing link to the real login page sends members on to a look-alike site the moment they sign in.',
    proof: {
      request: 'GET https://aamlcolorado.org/login?redirect=https://example.com/  then sign in as a throwaway member',
      result: '302 Location: https://example.com/',
    },
    fix: '/login?redirect= only accepts same-site paths.',
    could_break: 'Links that send members back to a page after login: same-site paths still work.',
    decision_id: 'D6',
  },
  {
    id: 'F7',
    section: 'security',
    bucket: 'codebase',
    title: 'Any logged-in user can delete any uploaded file',
    severity: 'login',
    exposed: 'Anyone with a login, including the shared guest account, can delete every image and document on media.cofamilylaw.org.',
    proof: {
      request: 'POST /.netlify/functions/r2-delete {"key":"uploads/test-probe.png"}  as the throwaway guest user, on a file the probe uploaded as admin',
      result: '200 {"deleted":true}',
    },
    fix: 'Non-staff can only delete their own uploads; staff can delete any.',
    could_break: 'Attorneys removing files they uploaded: still allowed.',
    decision_id: 'D7',
  },
  {
    id: 'F8',
    section: 'security',
    bucket: 'live_config',
    title: 'Auth site_url is http://localhost:3000',
    severity: 'login',
    exposed: 'Invite and reset links fall back to localhost, and the redirect allow-list is empty, so any URL is accepted.',
    proof: {
      request: 'GET /v1/projects/echldzywqxjgpgwcrdqc/config/auth  (management API)',
      result: '{"site_url":"http://localhost:3000","uri_allow_list":""}',
    },
    fix: 'site_url → https://admin.cofamilylaw.org; allow-list = the admin and the public site.',
    could_break: 'Nothing that works today: links built from site_url are already broken.',
    decision_id: 'D8',
  },
  {
    id: 'C1',
    section: 'integrity',
    bucket: 'cascade',
    title: 'Deleting a user deletes every post they wrote',
    severity: 'data_loss',
    exposed: 'Removing an editor who leaves takes their published blog posts, CMS entries and uploads with them.',
    proof: {
      request: 'BEGIN; DELETE FROM users WHERE id = <Ryan, editor>; count what went; ROLLBACK;',
      result: 'blog_posts 14, cms_entries 6, media_files 37 deleted. Rolled back.',
    },
    fix: 'blog_posts.author_id becomes ON DELETE SET NULL: the posts stay, the author goes blank.',
    could_break: 'Post pages that show the author’s name show none for a deleted author.',
    decision_id: 'D9',
    cascade: {
      constraint_name: 'blog_posts_author_id_fkey',
      from_table: 'blog_posts',
      to_table: 'users',
      label: 'bad',
      why: 'Posts are records of things that happened; they outlive the person who wrote them.',
      delete_count: {
        sample: 'Ryan (editor, 14 posts)',
        tables: [
          { table: 'blog_posts', rows: 14 },
          { table: 'cms_entries', rows: 6 },
          { table: 'media_files', rows: 37 },
        ],
      },
    },
  },
  {
    id: 'C2',
    section: 'integrity',
    bucket: 'cascade',
    title: 'Deleting a user deletes their notification settings',
    severity: 'data_loss',
    exposed: 'Only the person’s own settings go: nothing anyone else relies on.',
    proof: {
      request: 'BEGIN; DELETE FROM users WHERE id = <throwaway member>; count what went; ROLLBACK;',
      result: 'user_notification_settings 1 deleted. Rolled back.',
    },
    fix: 'Nothing: keep the cascade.',
    could_break: 'Nothing.',
    decision_id: 'D10',
    cascade: {
      constraint_name: 'user_notification_settings_user_id_fkey',
      from_table: 'user_notification_settings',
      to_table: 'users',
      label: 'good',
      why: 'The rows only describe the person: their own config.',
      delete_count: { sample: 'a throwaway member', tables: [{ table: 'user_notification_settings', rows: 1 }] },
    },
  },
  {
    id: 'D1',
    section: 'integrity',
    bucket: 'drift',
    title: 'One live migration has no file in the repo',
    severity: 'data_loss',
    exposed: 'A rebuild from the repo would lose the blog_posts definer-function policy, and the public blog would empty again.',
    proof: {
      request: 'supabase_migrations.schema_migrations vs AAML-backend-app/supabase/migrations',
      result: '78 live, 77 files: 20261023215000_blog_posts_definer_check is live only.',
    },
    fix: 'Add the file to the repo exactly as applied (no re-run).',
    could_break: 'Nothing: the database does not change.',
    decision_id: 'D11',
  },
  {
    id: 'B1',
    section: 'integrity',
    bucket: 'backups',
    title: 'Daily backups only, kept 7 days',
    severity: 'data_loss',
    exposed: 'A bad delete found after a week, or anything between two nightly backups, cannot be brought back.',
    proof: {
      request: 'GET /v1/projects/echldzywqxjgpgwcrdqc/database/backups  (management API)',
      result: '{"pitr_enabled":false,"backups":[7 daily, newest 2026-10-23 03:12 UTC]}',
    },
    fix: 'Turn on point-in-time recovery (a paid add-on, about $100 a month).',
    could_break: 'Nothing; it costs money.',
    decision_id: 'D12',
  },
];

const findingDecision = (f: SecurityFinding, recommendation: string, why: string): Decision => ({
  id: f.decision_id,
  question: `${f.id}: ${f.title}`,
  options: f.bucket === 'cascade' ? ['Change it', 'Good cascade, keep', 'Later'] : ['Fix', 'Skip', 'Later'],
  recommendation,
  why,
});

const DECISIONS: Decision[] = [
  findingDecision(FINDINGS[0], 'Fix', 'It was used on Sep 27: an outside account made itself admin this way.'),
  findingDecision(FINDINGS[1], 'Fix', 'It is the member list with phone numbers, open to anyone; the fix keeps every field the public site shows.'),
  findingDecision(FINDINGS[2], 'Fix', 'No reason for anon to touch it at all.'),
  findingDecision(FINDINGS[3], 'Fix', 'Free to fix and the hook has to be rotated anyway.'),
  findingDecision(FINDINGS[4], 'Fix', 'It mails every admin from our address with no login.'),
  findingDecision(FINDINGS[5], 'Fix', 'A one-line check; open redirects are what phishing kits look for.'),
  findingDecision(FINDINGS[6], 'Fix', 'The shared guest login makes this nearly public.'),
  findingDecision(FINDINGS[7], 'Fix', 'Config only, and it is already broken.'),
  findingDecision(FINDINGS[8], 'Change it', 'Posts are history; deleting a person should never delete what they published.'),
  findingDecision(FINDINGS[9], 'Good cascade, keep', 'Only the person’s own settings go.'),
  findingDecision(FINDINGS[10], 'Fix', 'The file is the only copy of a policy the public blog depends on.'),
  findingDecision(FINDINGS[11], 'Later', 'Worth it for a members site, but it is a spend, not a hole.'),
];

export const SECURITY_AUDIT_SUMMARY: OperatorSummary = {
  spec: {
    title: 'Security Audit — AAML',
    greeting:
      'Hey Kyle, AAML is done: eight proven holes, six of them open to anyone with no login, two cascades, one drift and the backups. Each row has its proof; answer Fix, Skip or Later on the row and approve when you’re happy.',
    sources: [{ id: 'screen', kind: 'other', label: 'Cockpit · Security → Audits · AAML (db:echldzywqxjgpgwcrdqc)' }],
    requests: [{ id: 'R1', source: 'screen', kind: 'build', text: 'Audit the AAML system: database, both repos and both sites.' }],
    actions: [{ request: 'R1', text: 'Fix what you mark Fix: database changes live after their checks, code as PRs on both repos.' }],
    repo: { name: 'eczzz/AAML-backend-app + eczzz/AAML-colorado-chapter', note: 'Admin app and public chapter site.' },
    db: { prod: 'echldzywqxjgpgwcrdqc', preview: null, note: 'No preview database; dry runs are BEGIN … ROLLBACK on live.' },
    need_to_know: [
      'F2’s fix must wait for the chapter event page change to deploy, or logged-out event pages lose the organizer card.',
      'Two flows send email and are not tested end to end: invites and password resets. They are listed in the build.',
    ],
    decisions: DECISIONS,
    approach: [
      'Every request above was run as a stranger with the public anon key, or as a throwaway user deleted the same run.',
      'Database fixes: rollback file first, dry run as each role, before/after diff, then live.',
    ],
    parked: [
      'The live service_role key is in git history (15 seed scripts since Apr 2). You said not now on Oct 24; rotating it touches both Netlify sites.',
      'No Content-Security-Policy header on either site. Framing and nosniff are set.',
      'Legacy lawyers and cms_aaml tables still exist, read-only to staff; nothing uses them.',
    ],
    findings: FINDINGS,
  },
  build: {
    pr_url: 'https://github.com/eczzz/AAML-backend-app/pull/57',
    preview_url: 'https://deploy-preview-57--aaml-backend.netlify.app',
    db_written: '4 migrations live on echldzywqxjgpgwcrdqc (stamped, 78 = 78)',
    deviations: ['F2 went live after chapter #46 deployed, as planned, but needed a second migration: see the rolled-back diff.'],
    checks: [
      { request: 'F1', applied: 'live', before: 'Signup with roles in metadata returned an admin.', after: 'Signup returns 422; a non-admin role change returns 403.', how_to_check: 'Re-run the F1 request: 422.' },
      { request: 'F2', applied: 'live', before: 'Anon read email, phone and roles for 214 users.', after: 'Anon reads names and avatars only; email returns permission denied.', how_to_check: 'GET /rest/v1/users?select=email with the anon key: 401.' },
      { request: 'F3', applied: 'live', before: 'Anon could insert, change and delete recipients.', after: 'Anon gets 401 on every operation; admins unchanged.', how_to_check: 'Re-run the F3 insert as anon.' },
      { request: 'F4', applied: 'live', before: 'build_hook_url readable with the anon key.', after: 'Hidden from anon and non-staff; old hook deleted, new one in place.', how_to_check: 'Publish from the admin still rebuilds the site.' },
      { request: 'F5', applied: 'pr', before: 'Public function mailed admins any HTML.', after: 'Sends only for a fresh pending row, escaped.', how_to_check: 'After merge: supabase functions deploy notify-admin-pending.' },
      { request: 'F6', applied: 'pr', before: '/login?redirect= followed any URL.', after: 'Only same-site paths; others go to /members.', how_to_check: 'Chapter #48 preview: /login?redirect=https://example.com/.' },
      { request: 'F7', applied: 'pr', before: 'Any login could delete any file.', after: 'Non-staff delete only their own uploads.', how_to_check: 'PR preview as the guest login: 403 on another user’s file.' },
      { request: 'F8', applied: 'live', before: 'site_url localhost, empty allow-list.', after: 'site_url admin.cofamilylaw.org; allow-list = both sites.', how_to_check: 'Auth settings in Supabase.' },
      { request: 'C1', applied: 'live', before: 'Deleting Ryan took 14 posts, 6 entries, 37 files.', after: 'Delete keeps all 57 rows with the author blank.', how_to_check: 'BEGIN … ROLLBACK delete of Ryan: 0 rows lost.' },
      { request: 'D1', applied: 'pr', before: '78 live, 77 files.', after: '78 = 78.', how_to_check: 'Drift check after merge.' },
    ],
    verification: {
      snapshot: {
        pages: 41,
        queries: 63,
        diffs: [{ what: 'blog_posts as anon (/blog, /news, homepage)', before: '12 posts', after: 'permission denied for table users' }],
        passed: true,
      },
      walkthroughs: [
        { role: 'admin', steps: 14, passed: true },
        { role: 'editor', steps: 11, passed: true },
        { role: 'attorney', steps: 9, passed: true, note: 'Settings now shows My Account, so attorneys can change their password.' },
        { role: 'member (public site)', steps: 6, passed: true },
        { role: 'guest login', steps: 4, passed: true },
      ],
      not_tested_email: ['Add User invite email', 'Password reset email', 'Pending-post review email to admins'],
      test_users_created: 5,
      test_users_left: 0,
      rolled_back: ['blog_posts as anon (/blog, /news, homepage)'],
    },
  },
  approval: {
    live_url: 'https://admin.cofamilylaw.org',
    client_note:
      'We finished a security review of the AAML admin and public site. We closed six ways a stranger could get in or read member details, made sure deleting a user keeps their posts, and checked every page and role still works.',
    confirmed: [{ request: 'R1', status: 'live', note: 'PRs #57 and chapter #48 merged and deployed; edge function deployed.' }],
    follow_ups: ['Rotate the service_role key (parked in the spec).', 'Point-in-time recovery: answered Later.'],
  },
};

const answered = (summary: OperatorSummary): OperatorSummary => {
  const answers = ['Fix', 'Fix', 'Fix', 'Fix', 'Fix', 'Fix', 'Fix', 'Fix', 'Change it', 'Good cascade, keep', 'Fix', 'Later'];
  return { ...summary, spec: { ...summary.spec!, decisions: summary.spec!.decisions.map((d, i) => ({ ...d, answer: answers[i] })) } };
};

/** At Spec ready the calls are still open; once the build exists they carry Kyle's answers. */
export function securityAuditSummaryAt(stage: 'spec_ready' | 'build_ready' | 'complete'): OperatorSummary {
  return stage === 'spec_ready' ? SECURITY_AUDIT_SUMMARY : answered(SECURITY_AUDIT_SUMMARY);
}

const BASE: AiJob = {
  id: 'sample-security-audit',
  task_id: 'sample-task',
  client_id: null,
  card_id: null,
  status: 'spec_ready',
  kind: 'fulfillment',
  task_type: 'security_audit',
  security_system_id: null,
  title: 'Security Audit — AAML',
  spec_body: '',
  report_body: '',
  approval_body: '',
  revision_note: '',
  handoff_notes: [],
  queue_note: '',
  brief: {},
  auto_approve: false,
  version: 2,
  claimed_by: null,
  claimed_at: null,
  created_by: null,
  created_at: '2026-10-24T10:20:00.000-06:00',
  updated_at: '2026-10-24T11:40:00.000-06:00',
  queue_position: 1,
  lock_reason: '',
  blocked_by_job_id: null,
  db_ref: null,
  parked_from: null,
  migration_version: null,
  model: null,
  operator_summary: null,
} as AiJob;

export function securityAuditJobAt(stage: 'spec_ready' | 'build_ready' | 'complete'): AiJob {
  return { ...BASE, status: stage, version: stage === 'spec_ready' ? 2 : stage === 'build_ready' ? 3 : 4 } as AiJob;
}
